import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { SOCKET_ID_VERSION_CONFIG_KEY } from "../../utils/dynamicSockets/dynamicSocketId";
import type { SocketDefinition } from "../../types";

const ColumnTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
]);

const ColumnSnapshotSchema = z.object({
  name: z.string().min(1),
  type: ColumnTypeSchema,
});

const FrameCellValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);

/**
 * Output socket ids the step always owns. A column of the same name cannot get
 * a per-column output socket — it would shadow one of these — so the config
 * snapshot rejects it (see `configSchema` below) and the socket generator
 * throws. Keep in sync with the named keys of `outputSchema`.
 */
export const READ_FRAME_RESERVED_OUTPUT_SOCKET_IDS = [
  "csv",
  "rows",
  "columns",
  "rowCount",
  "frameNumber",
  "versionNumber",
] as const;

const RESERVED_SET: ReadonlySet<string> = new Set(
  READ_FRAME_RESERVED_OUTPUT_SOCKET_IDS,
);

/**
 * The step's six always-present output sockets, exactly as they were inferred
 * from `outputSchema` before per-column sockets existed — this step has
 * existing consumers, and the change is purely additive. Shared by the backend
 * generator and the editor so both sides emit one identical socket surface.
 */
export function readFrameReservedOutputSockets(): SocketDefinition[] {
  return [
    {
      id: "csv",
      name: "CSV",
      type: "string",
      required: false,
      description: "The whole frame serialized as CSV, including a header row",
    },
    {
      id: "rows",
      name: "Rows",
      type: "array<json>",
      required: false,
      description:
        "Positional cell arrays, one per row, ordered to match `columns`",
    },
    {
      id: "columns",
      name: "Columns",
      type: "array<string>",
      required: false,
      description: "The frame's column names, in positional order",
    },
    {
      id: "rowCount",
      name: "Row Count",
      type: "number",
      required: false,
      description: "Number of rows in the frame after filtering",
    },
    {
      id: "frameNumber",
      name: "Frame Number",
      type: "number",
      required: false,
      description: "Frame number the values were read from",
    },
    {
      id: "versionNumber",
      name: "Version Number",
      type: "number",
      required: false,
      description: "Table version the frame belongs to",
    },
  ];
}

/**
 * The per-column output socket for one column of the frame: an array of that
 * column's value for EVERY row, in frame row order.
 */
export function readFrameColumnOutputSocket(params: {
  columnName: string;
  cellSocketType: string;
  displayName: string;
}): SocketDefinition {
  return {
    id: params.columnName,
    name: params.displayName,
    type: `array<${params.cellSocketType}>` as SocketDefinition["type"],
    required: false,
    description: `Every row's value for column "${params.columnName}", in frame row order. Same length as every other per-column array, so wiring several into one per-row step zips them by row.`,
  };
}

/**
 * A column's per-column output socket id is its column name VERBATIM — the same
 * rule the step's per-column filter INPUT sockets already use, so one column
 * reads the same on both sides of the node. Socket ids are opaque strings
 * matched exactly by `collectStepInputs`, so non-identifier names
 * (`hub_or_dc_location_y_n`, `satellite_code_1`) need no escaping; escaping
 * would only risk making two distinct columns collide.
 *
 * Because there is no escaping, the only ways an id can collide are a duplicate
 * column name or a reserved name — both rejected loudly here rather than
 * silently shadowing a socket.
 */
export function assertReadFrameColumnNamesUsable(
  columnNames: readonly string[],
): void {
  const reserved = columnNames.filter((name) => RESERVED_SET.has(name));
  if (reserved.length > 0) {
    throw new Error(
      `stateful_table_read_frame: column name(s) ${reserved
        .map((n) => `"${n}"`)
        .join(", ")} collide with the step's reserved output socket ids ` +
        `(${READ_FRAME_RESERVED_OUTPUT_SOCKET_IDS.join(", ")}). ` +
        `Rename the column in the stateful table — a per-column socket must not shadow a reserved one.`,
    );
  }

  assertReadFrameColumnNamesUnique(columnNames);
}

/**
 * Refuse a snapshot with two columns of the same name.
 *
 * Unlike a reserved-name collision, this one the `::dynamic` marker cannot fix:
 * both columns derive the same id either way. A real stateful table cannot
 * produce it, so a snapshot that has it is malformed and worth rejecting at
 * config-save time rather than quietly giving the second column a suffixed id.
 */
export function assertReadFrameColumnNamesUnique(
  columnNames: readonly string[],
): void {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const name of columnNames) {
    if (seen.has(name)) duplicates.add(name);
    seen.add(name);
  }
  if (duplicates.size > 0) {
    throw new Error(
      `stateful_table_read_frame: duplicate column name(s) ${[...duplicates]
        .map((n) => `"${n}"`)
        .join(", ")} in the schema snapshot. ` +
        `Per-column sockets are keyed by column name, so duplicates would shadow each other.`,
    );
  }
}

export const statefulTableReadFrameStep = defineStepConfig({
  type: "stateful_table_read_frame",
  name: "Stateful Table Read Frame",
  description:
    "Read the latest frame from a stateful table, optionally filtered by per-column inputs (exact match, AND-joined). Outputs CSV, a structured rows array, and one same-length array per column so downstream per-row steps can lockstep-fan out.",
  category: "Data Access",

  inputSchema: z.record(z.string(), z.unknown()),

  // The per-column outputs are dynamic (their ids come from `config.columns`),
  // so they cannot be named here — hence `looseObject` rather than `object`.
  // This is load-bearing, not cosmetic: `executeStep` parses handler output
  // through this schema and a plain `z.object` STRIPS unknown keys, so every
  // per-column array would be dropped before it ever reached an edge.
  // (`stateful_table_lookup_row` has this exact bug today — its per-column
  // values are stripped by its strict `z.object` output schema.) The arrays'
  // shape is guaranteed structurally instead, by `projectFrameColumns`.
  outputSchema: z.looseObject({
    csv: z.string(),
    rows: z.array(z.array(FrameCellValueSchema)),
    columns: z.array(z.string()),
    rowCount: z.number().int().min(0),
    frameNumber: z.number().int().min(1),
    versionNumber: z.number().int().min(1),
  }),

  configSchema: z.object({
    tableDefinitionId: z
      .string()
      .uuid()
      .describe("ID of the stateful table to read from"),
    columns: z
      .array(ColumnSnapshotSchema)
      .min(1)
      .check((ctx) => {
        // Fail at config-save time, so a snapshot whose column names cannot be
        // projected into sockets is never persisted in the first place.
        try {
          // Only uniqueness is checked here. A column named after a reserved
          // output socket used to be rejected too, which made a stateful table
          // with a `rows` column unusable by this step at all; the `::dynamic`
          // marker now gives that column its own id, so the snapshot is fine.
          assertReadFrameColumnNamesUnique(ctx.value.map((c) => c.name));
        } catch (error) {
          ctx.issues.push({
            code: "custom",
            input: ctx.value,
            message: error instanceof Error ? error.message : String(error),
          });
        }
      })
      .describe(
        "Snapshot of the table's column schema at design time. Drives the per-column filter input sockets and the per-column array output sockets.",
      ),
    // Declared so z.object() does not strip the stamp on parse. `executeStep`
    // parses the persisted config through this schema before handing it to the
    // handler, and the handler derives its payload keys from the version: drop
    // the stamp and it reads filters and writes per-column arrays under bare
    // ids for sockets the canvas marked `::dynamic`.
    [SOCKET_ID_VERSION_CONFIG_KEY]: z
      .number()
      .int()
      .optional()
      .describe(
        "Socket-id scheme this step was created under. Absent means the legacy scheme, where a column name IS its socket id.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: { maximumAttempts: 2 },
  },
});
